import { StatusId, Rang, TabKind, TabDescriptor, OperationBC, CommandeRang } from '../types';
import {
  parseRang,
  commandesParRang,
  commandeDuRang,
  premierRang,
  estPremierRang,
  estRangEcarte,
  rangCourant,
} from './rangs';

// Logique PURE des onglets acheteur-BC : visibilite des rangs, gating
// (disabled/inactive) et onglet actif initial. Aucune dependance React :
// testable, isolee, reutilisable. Le hook useOperationTabs n'est que le cablage URL.
//
// Les rangs sont ceux des COMMANDES de l'operation (domain/rangs), jamais la suite 1..3 : une
// operation peut n'avoir qu'un rang 2, ou les rangs {1,3}. Aucune regle ici ne nomme un numero de
// rang ; elle parle de premier rang, de rang precedent et de rang courant.

interface RangState {
  consulte: boolean;
  offreDisponible: boolean;
  accepte: boolean;
  refuse: boolean;
}

const hasArticles = (cmd: CommandeRang): boolean => Array.isArray(cmd.articles) && cmd.articles.length > 0;

// Index de la commande acceptee (Valide = 7) dans l'ordre des rangs, -1 s'il n'y en a pas.
const indexCommandeAcceptee = (commandes: CommandeRang[]): number =>
  commandes.findIndex((c) => c.id_status === StatusId.Valide);

// --- 1. Visibilite des rangs (cascade) ----------------------------------------
// Un rang n'apparait qu'une fois le precedent refuse/expire ; jamais apres un
// rang accepte. Expire == refuse pour le deblocage du rang suivant.
export const computeVisibleRangs = (operation: OperationBC): Rang[] => {
  const commandes = commandesParRang(operation);
  if (commandes.length === 0) return [];
  const rangs = commandes.map((c) => parseRang(c.rang));

  const statusNorm = (operation.status?.nom || '').toLowerCase();
  const isTerminee = [StatusId.Valide, StatusId.Refuse, StatusId.Expire].includes(
    operation.id_status as StatusId,
  );
  const visibles = new Set<number>();
  const accepte = indexCommandeAcceptee(commandes);

  if (statusNorm.includes('brouillon')) {
    visibles.add(rangs[0]);
  } else if (isTerminee) {
    // Retenu : tous les rangs jusqu'au retenu inclus. Aucun retenu (refus / expiration globale) :
    // tous les rangs consultes.
    (accepte >= 0 ? rangs.slice(0, accepte + 1) : rangs).forEach((r) => visibles.add(r));
  } else {
    // En cours : on descend la liste tant que le rang precedent est ecarte.
    for (let i = 0; i < commandes.length; i += 1) {
      visibles.add(rangs[i]);
      if (!estRangEcarte(commandes[i].id_status)) break;
    }
    // Un rang accepte est visible avec tous ceux qui le precedent.
    if (accepte >= 0) rangs.slice(0, accepte + 1).forEach((r) => visibles.add(r));
  }

  return Array.from(visibles).sort((a, b) => a - b);
};

// --- 2. Construction des descripteurs (ordre = compatibilite index ?tab) ------
const buildBaseDescriptors = (visibleRangs: Rang[]): TabDescriptor[] => {
  const list: TabDescriptor[] = [
    { key: 'historique', kind: 'historique', label: 'Historique', disabled: false, inactive: false },
  ];
  visibleRangs.forEach((rang) => {
    list.push({ key: `consultation-${rang}`, kind: 'consultation', rang, label: `Consultation Rang ${rang}`, disabled: false, inactive: false });
    list.push({ key: `offre-${rang}`, kind: 'offre', rang, label: `Offre Rang ${rang}`, disabled: false, inactive: false });
  });
  list.push({ key: 'notification', kind: 'notification', label: 'Notification et signature', disabled: false, inactive: false });
  list.push({ key: 'messagerie', kind: 'messagerie', label: 'Messagerie', disabled: false, inactive: false });
  list.push({ key: 'evaluation', kind: 'evaluation', label: 'Évaluation', disabled: false, inactive: false });
  return list;
};

// --- 3. Etat par rang (entree du gating) --------------------------------------
const computeRangStates = (operation: OperationBC): Map<number, RangState> => {
  const states = new Map<number, RangState>();
  const commandes = commandesParRang(operation);
  commandes.forEach((cmd) => {
    states.set(parseRang(cmd.rang), { consulte: false, offreDisponible: false, accepte: false, refuse: false });
  });

  const isTransferred = operation.transferred === true;
  const status = operation.status?.nom;
  const statusNorm = (status || '').toLowerCase();
  const currentRang = rangCourant(operation);

  commandes.forEach((cmd) => {
    const state = states.get(parseRang(cmd.rang));
    if (!state) return;
    state.consulte = true;
    if (!isTransferred && hasArticles(cmd)) state.offreDisponible = true;
    switch (cmd.id_status) {
      case StatusId.Valide:
        state.accepte = true;
        state.offreDisponible = true;
        break;
      case StatusId.Refuse:
      case StatusId.Expire: // expire == refuse pour la cascade
        state.refuse = true;
        state.offreDisponible = true;
        break;
      case StatusId.AttenteAnalyse:
        state.offreDisponible = true;
        break;
      default:
        break;
    }
  });

  if (isTransferred) {
    // Un rang ecarte ouvre la consultation du rang SUIVANT DE L'OPERATION (pas rang + 1).
    commandes.forEach((cmd, index) => {
      const state = states.get(parseRang(cmd.rang));
      const suivante = commandes[index + 1];
      const next = suivante ? states.get(parseRang(suivante.rang)) : undefined;
      if (state?.refuse && next) next.consulte = true;
    });
    commandes.forEach((cmd) => {
      if (cmd.id_status === StatusId.PropositionFaite) {
        const state = states.get(parseRang(cmd.rang));
        if (state) state.offreDisponible = true;
      }
    });
  }

  if (status === 'À accepter') {
    const state = states.get(currentRang);
    if (state) state.offreDisponible = true;
  }
  if (!isTransferred && status === 'À analyser') {
    commandes.forEach((cmd) => {
      const state = states.get(parseRang(cmd.rang));
      if (state && (hasArticles(cmd) || estRangEcarte(cmd.id_status))) state.offreDisponible = true;
    });
  }
  if (statusNorm === 'refusé' || statusNorm === 'refuse') {
    const state = states.get(currentRang);
    if (state) state.offreDisponible = true;
  }

  return states;
};

// --- 4. Gating : une regle simple par type d'onglet ---------------------------
// Chaque fonction repond a UNE question : "cet onglet est-il desactive ?".
interface GateContext {
  operation: OperationBC;
  states: Map<number, RangState>;
  isBrouillon: boolean;
  isTransferred: boolean;
  currentRang: number;
}

const consultationDisabled = (rang: Rang, ctx: GateContext): boolean => {
  const state = ctx.states.get(rang);
  const accessible = Boolean(state?.consulte) || rang === ctx.currentRang;
  return !accessible;
};

const offreDisabled = (rang: Rang, ctx: GateContext): boolean => {
  const state = ctx.states.get(rang);
  const cmd = commandeDuRang(ctx.operation, rang);
  if (ctx.isTransferred) {
    const offrePrestataireDispo =
      !!cmd && [StatusId.PropositionFaite, StatusId.Valide, StatusId.Refuse].includes(cmd.id_status);
    return !(state?.offreDisponible || offrePrestataireDispo);
  }
  // Rang de repli encore en brouillon : sa consultation n'est pas lancee, il n'a pas d'offre.
  if (!estPremierRang(ctx.operation, rang) && cmd?.id_status === StatusId.BrouillonAcheteur) return true;
  return !state?.offreDisponible;
};

// En brouillon : seuls Historique, Consultation du premier rang, Messagerie, Evaluation accessibles.
const allowedInBrouillon = (tab: TabDescriptor, premier: number): boolean =>
  tab.kind === 'historique' ||
  (tab.kind === 'consultation' && tab.rang === premier) ||
  tab.kind === 'messagerie' ||
  tab.kind === 'evaluation';

const applyGating = (descriptors: TabDescriptor[], operation: OperationBC): TabDescriptor[] => {
  const ctx: GateContext = {
    operation,
    states: computeRangStates(operation),
    isBrouillon: (operation.status?.nom || '').toLowerCase().includes('brouillon'),
    isTransferred: operation.transferred === true,
    currentRang: rangCourant(operation),
  };
  const premier = premierRang(operation);

  // Evaluation : active, mais grisee tant qu'aucune commande n'est validee (acceptee = 7).
  const evalUnlocked = (operation.commandes || []).some((c) => c.id_status === StatusId.Valide);

  return descriptors.map((tab) => {
    let disabled = false;
    if (tab.kind === 'messagerie') {
      // Messagerie privee : active uniquement quand l'operation est validee (commande acceptee = 7).
      // Grisee (non "a venir") tant que le prestataire n'est pas retenu.
      disabled = operation.id_status !== StatusId.Valide;
    } else if (tab.kind === 'notification') {
      // Notification et signature : accessible UNIQUEMENT quand le bon de commande est accepte/signe
      // des deux cotes (operation au statut Valide=7), comme la messagerie.
      disabled = operation.id_status !== StatusId.Valide;
    } else if (tab.kind === 'evaluation') {
      disabled = !evalUnlocked;
    } else if (ctx.isBrouillon) {
      disabled = !allowedInBrouillon(tab, premier);
    } else if (tab.kind === 'consultation' && tab.rang !== undefined) {
      disabled = consultationDisabled(tab.rang, ctx);
    } else if (tab.kind === 'offre' && tab.rang !== undefined) {
      disabled = offreDisabled(tab.rang, ctx);
    }

    return { ...tab, disabled, inactive: false };
  });
};

// --- 5. API publique du domaine -----------------------------------------------
export const buildOperationTabs = (operation: OperationBC): TabDescriptor[] =>
  applyGating(buildBaseDescriptors(computeVisibleRangs(operation)), operation);

// Onglet actif par defaut (hors URL ?tab) selon le statut de l'operation.
export const computeDefaultTabIndex = (descriptors: TabDescriptor[], operation: OperationBC): number => {
  const indexOf = (kind: TabKind, rang?: number) =>
    descriptors.findIndex((t) => t.kind === kind && (rang === undefined || t.rang === rang));

  const status = operation.status?.nom;
  const statusNorm = (status || '').toLowerCase();
  const isTransferred = operation.transferred === true;
  const commandes = commandesParRang(operation);
  const operationStatusId = operation.id_status;
  const premier = premierRang(operation);

  if (statusNorm.includes('brouillon')) {
    const i = indexOf('consultation', premier);
    return i === -1 ? 0 : i;
  }

  // Operation validee : on ouvre "Notifications et signature", pas l'Offre. Meme regle que le clic
  // depuis la liste des operations (acheteurRowTabKind) — sans ca, l'acheteur qui vient de signer et
  // dont l'URL ne porte pas de ?tab= retombait sur l'Offre au rechargement. Repli sur l'offre du rang
  // accepte si l'onglet notification n'existe pas dans ce jeu de descripteurs.
  if (operationStatusId === StatusId.Valide) {
    const notif = indexOf('notification');
    if (notif !== -1) return notif;
    const accepted = commandes.find((c) => c.id_status === StatusId.Valide);
    if (accepted) {
      const i = indexOf('offre', parseRang(accepted.rang));
      if (i !== -1) return i;
    }
  }

  if (!isTransferred && (status === 'À analyser' || operationStatusId === StatusId.AAnalyser)) {
    if (commandes.length === 0) return 0;
    // Offre du premier rang encore en lice (tous ceux d'avant sont ecartes) s'il porte des articles,
    // sinon l'offre du premier rang.
    const enLice = commandes.find((c) => !estRangEcarte(c.id_status));
    if (enLice && hasArticles(enLice)) {
      const i = indexOf('offre', parseRang(enLice.rang));
      if (i !== -1) return i;
    }
    const i = indexOf('offre', premier);
    return i === -1 ? 0 : i;
  }

  if (
    isTransferred &&
    (status === 'À analyser' ||
      status === "En attente de décision de l'acheteur" ||
      operationStatusId === StatusId.AttenteAnalyse)
  ) {
    const firstActive = commandes.find((c) => !estRangEcarte(c.id_status));
    const target = firstActive
      ? indexOf('consultation', parseRang(firstActive.rang))
      : indexOf('consultation', premier);
    return target === -1 ? 0 : target;
  }

  return 0;
};
